import type { Lesson, Schedule } from "./types";
import type { PersonalPrefs, ScheduleDiff } from "../shared/lessons";
import type { RecentGroup } from "./Groups";
import type { AcademicCalendar } from "../shared/weeks";
const KEY = "psu-schedules-v1";
export function readSaved(): Schedule[] {
  try {
    const value = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(value)
      ? value.filter(
          (s) =>
            s &&
            Array.isArray(s.groups) &&
            Array.isArray(s.lessons) &&
            s.source,
        )
      : [];
  } catch {
    return [];
  }
}
export function saveSchedule(schedule: Schedule) {
  const list = [
    schedule,
    ...readSaved().filter((s) =>
      s.source.fileId
        ? s.source.fileId !== schedule.source.fileId
        : s.id !== schedule.id,
    ),
  ].slice(0, 12);
  localStorage.setItem(KEY, JSON.stringify(list));
  return list;
}
export function readPreference() {
  try {
    return JSON.parse(localStorage.getItem("psu-preference") || "null");
  } catch {
    return null;
  }
}
export function savePreference(id: string, group: string) {
  try {
    localStorage.setItem("psu-preference", JSON.stringify({ id, group }));
  } catch {
    /* Preference saving must not hide a loaded schedule. */
  }
}
const CALENDAR = "psu-calendar-v1";
export function readCalendar(): AcademicCalendar | null {
  try {
    const value = JSON.parse(localStorage.getItem(CALENDAR) || "null");
    return value && Array.isArray(value.weeks) && Array.isArray(value.bells)
      ? value
      : null;
  } catch {
    return null;
  }
}
export function saveCalendar(calendar: AcademicCalendar) {
  try {
    localStorage.setItem(CALENDAR, JSON.stringify(calendar));
  } catch {
    /* The calendar is small; a full storage only costs offline week labels. */
  }
}
const DISMISSED = "psu-dismissed-updates";
/** Documents the student chose not to switch to; offers for them stay hidden. */
export function readDismissed(): string[] {
  try {
    const value = JSON.parse(localStorage.getItem(DISMISSED) || "[]");
    return Array.isArray(value) ? value.filter((v) => typeof v === "string") : [];
  } catch {
    return [];
  }
}
export function dismissUpdate(id: string) {
  try {
    localStorage.setItem(
      DISMISSED,
      JSON.stringify([id, ...readDismissed().filter((v) => v !== id)].slice(0, 50)),
    );
  } catch {
    /* Without storage the offer simply returns next launch. */
  }
}
const PERSONAL = "psu-personal-v1";
/** Per document and group: the student's subgroup and hidden lessons. Never leaves the device. */
export type PersonalMap = Record<string, PersonalPrefs>;
export function readPersonal(): PersonalMap {
  try {
    const value = JSON.parse(localStorage.getItem(PERSONAL) || "{}");
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
  } catch {
    return {};
  }
}
export function savePersonal(map: PersonalMap) {
  try {
    localStorage.setItem(PERSONAL, JSON.stringify(map));
  } catch {
    /* Settings stay for this session only. */
  }
}
/** The document a schedule came from, stable across republished versions. */
export const documentKey = (s: Schedule) =>
  s.source.fileId || `upload:${s.source.name}`;

const CHANGES = "psu-last-changes-v1";
export interface ChangeRecord {
  document: string;
  group: string;
  name: string;
  at: string;
  from?: string;
  to?: string;
  diff: ScheduleDiff<Lesson>;
}
export function readChanges(): ChangeRecord[] {
  try {
    const value = JSON.parse(localStorage.getItem(CHANGES) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}
export function saveChanges(record: ChangeRecord) {
  try {
    localStorage.setItem(
      CHANGES,
      JSON.stringify(
        [
          record,
          ...readChanges().filter(
            (r) => r.document !== record.document || r.group !== record.group,
          ),
        ].slice(0, 12),
      ),
    );
  } catch {
    /* The banner still shows the changes for this session. */
  }
}
const EXAMS = "psu-exams-v1";
export interface Exam {
  date: string;
  start: string;
  end: string | null;
  type: string;
  subject: string;
  teachers: string[];
  rooms: string[];
  document: { id: string; name: string; period?: string };
}
/** Last known exams per group, so the countdown works offline. */
export function readExams(key: string): Exam[] {
  try {
    const value = JSON.parse(localStorage.getItem(EXAMS) || "{}")[key];
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}
export function saveExams(key: string, exams: Exam[]) {
  try {
    const all = JSON.parse(localStorage.getItem(EXAMS) || "{}");
    all[key] = exams;
    localStorage.setItem(EXAMS, JSON.stringify(all));
  } catch {
    /* Offline countdown is a convenience. */
  }
}
const RECENT_GROUPS = "psu-recent-groups-v1";
export function readRecentGroups(): RecentGroup[] {
  try {
    const value = JSON.parse(localStorage.getItem(RECENT_GROUPS) || "[]");
    return Array.isArray(value) ? value.filter((v) => v && v.group && v.document) : [];
  } catch {
    return [];
  }
}
export function rememberGroup(item: RecentGroup) {
  try {
    const list = [
      item,
      ...readRecentGroups().filter(
        (r) => r.group !== item.group || r.document !== item.document,
      ),
    ].slice(0, 8);
    localStorage.setItem(RECENT_GROUPS, JSON.stringify(list));
    return list;
  } catch {
    return readRecentGroups();
  }
}
export type Theme = "system" | "light" | "dark";
const THEME = "psu-theme";
// index.html reads the same key before the first paint, so the page never flashes the other theme.
export function readTheme(): Theme {
  try {
    const value = localStorage.getItem(THEME);
    return value === "light" || value === "dark" ? value : "system";
  } catch {
    return "system";
  }
}
export function saveTheme(theme: Theme) {
  try {
    if (theme === "system") localStorage.removeItem(THEME);
    else localStorage.setItem(THEME, theme);
  } catch {
    /* The theme still applies for this session. */
  }
}
